import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/UI/Button';
import { ConfirmModal } from '../../../components/UI/ConfirmModal';
import { useToast } from '../../../components/UI/ToastProvider';
import { featureService } from '../../../services/featureService';
import { productModuleService } from '../../../services/productModuleService';
import type { Feature } from '../../../types/feature';
import type { ProductModule } from '../../../types/productModule';

type FormState = {
  id?: string;
  code: string;
  name: string;
  description: string;
  productModuleId: string;
  active: boolean;
};

const initialFormState: FormState = {
  code: '',
  name: '',
  description: '',
  productModuleId: '',
  active: true,
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { message?: string; error?: string; title?: string } } })?.response?.data;
  if (response?.error)
  {
    return response.error;
  }
  if (response?.message)
  {
    return response.message;
  }
  if (response?.title)
  {
    return response.title;
  }
  return fallback;
};

export const FeaturesPage: React.FC = () => {
  const { addToast } = useToast();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [productModules, setProductModules] = useState<ProductModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; item: Feature | null }>({
    open: false,
    item: null,
  });

  const loadData = async () => {
    try
    {
      setLoading(true);
      const [featuresData, modulesData] = await Promise.all([
        featureService.getAll(),
        productModuleService.getAll(),
      ]);
      setFeatures(featuresData);
      setProductModules(modulesData);
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao carregar features.'), 'error');
    }
    finally
    {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const filteredFeatures = useMemo(() => {
    const term = search.toLowerCase().trim();
    return features.filter((item) => {
      const matchesModule =
        moduleFilter === 'all' || item.productModuleId === moduleFilter;
      const matchesSearch =
        !term ||
        item.code.toLowerCase().includes(term) ||
        item.name.toLowerCase().includes(term) ||
        (item.productModuleCode?.toLowerCase().includes(term) ?? false);
      return matchesModule && matchesSearch;
    });
  }, [features, search, moduleFilter]);

  const openCreate = () => {
    setFormState({
      ...initialFormState,
      productModuleId: productModules[0]?.id ?? '',
    });
    setShowForm(true);
  };

  const openEdit = (item: Feature) => {
    setFormState({
      id: item.id,
      code: item.code,
      name: item.name,
      description: item.description ?? '',
      productModuleId: item.productModuleId,
      active: item.active,
    });
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    if (!formState.code.trim())
    {
      addToast('Informe o código da feature.', 'error');
      return false;
    }
    if (!formState.name.trim())
    {
      addToast('Informe o nome da feature.', 'error');
      return false;
    }
    if (!formState.productModuleId)
    {
      addToast('Selecione o módulo de produto.', 'error');
      return false;
    }
    return true;
  };

  const onSave = async () => {
    if (!validateForm())
    {
      return;
    }

    setSaving(true);
    try
    {
      const payload = {
        code: formState.code.trim().toUpperCase(),
        name: formState.name.trim(),
        description: formState.description.trim() || null,
        productModuleId: formState.productModuleId,
        active: formState.active,
      };

      if (formState.id)
      {
        await featureService.update(formState.id, { id: formState.id, ...payload });
        addToast('Feature atualizada com sucesso.', 'success');
      }
      else
      {
        await featureService.create(payload);
        addToast('Feature criada com sucesso.', 'success');
      }

      setShowForm(false);
      await loadData();
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Não foi possível salvar a feature.'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  const handleDelete = (item: Feature) => {
    setConfirmDelete({ open: true, item });
  };

  const confirmDeleteFeature = async () => {
    if (!confirmDelete.item)
    {
      return;
    }

    try
    {
      await featureService.delete(confirmDelete.item.id);
      addToast('Feature excluída com sucesso.', 'success');
      await loadData();
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Não foi possível excluir a feature.'), 'error');
    }
    finally
    {
      setConfirmDelete({ open: false, item: null });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Features</h1>
          <p className="text-sm text-gray-600">
            Capacidades licenciáveis associadas a cada módulo de produto.
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={productModules.length === 0}
          className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nova feature
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por código, nome ou módulo..."
          className="w-full rounded-md border border-gray-300 px-3 py-2"
        />
        <select
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2"
        >
          <option value="all">Todos os módulos</option>
          {productModules.map((module) => (
            <option key={module.id} value={module.id}>
              {module.code} — {module.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Carregando...</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Módulo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredFeatures.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">{item.code}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div>{item.name}</div>
                    {item.description && (
                      <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 font-mono">
                      {item.productModuleCode ?? '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        item.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.active ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => openEdit(item)} title="Editar">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(item)} title="Excluir">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredFeatures.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    Nenhuma feature encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900">
              {formState.id ? 'Editar feature' : 'Nova feature'}
            </h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Código *</label>
                <input
                  type="text"
                  value={formState.code}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono"
                  maxLength={80}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nome *</label>
                <input
                  type="text"
                  value={formState.name}
                  onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  maxLength={150}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                <textarea
                  value={formState.description}
                  onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  rows={2}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Módulo de produto *</label>
                <select
                  value={formState.productModuleId}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, productModuleId: e.target.value }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  <option value="">Selecione...</option>
                  {productModules.map((module) => (
                    <option key={module.id} value={module.id}>
                      {module.code} — {module.name}
                    </option>
                  ))}
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formState.active}
                  onChange={(e) => setFormState((prev) => ({ ...prev, active: e.target.checked }))}
                />
                Feature ativa
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={() => void onSave()}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.open}
        title="Confirmar exclusão"
        message={`Tem certeza que deseja excluir a feature "${confirmDelete.item?.name}"? Esta ação não poderá ser desfeita.`}
        onConfirm={() => void confirmDeleteFeature()}
        onCancel={() => setConfirmDelete({ open: false, item: null })}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};
